import ProductsApi from '@/scripts/api/products.api';
import { ProductSlimResponseDTO } from '@shared/DTO';
import { createDomElementHandler as el } from '@/scripts/utils/dom.utils';
import { createProductCardViewModel, createProductListingItem } from '@/scripts/components/ab-products/variants/productListingItem';
import type { AbProductSummaryElement } from '../ab-product-summary/abProductSummary.element';
import type { AbProductAffiliateBoxElement } from '../ab-product-affiliate-box/abProductAffiliateBox.element';
import type { AbProductsElement } from '@/scripts/components/ab-products/abProducts.element';
import type { AbStickyTocElement } from '@/scripts/components/ab-sticky-toc/abStickyToc.element';
import { GuideConfig, GuideSection, resolveGuideProduct } from './guideProduct.model';

export class AbBuyingGuideElement extends HTMLElement {
  #generation = 0;

  connectedCallback() { void this.#load(); }
  disconnectedCallback() { ++this.#generation; }

  async #load() {
    const generation = ++this.#generation;
    try {
      const config: GuideConfig = JSON.parse(this.querySelector('script[data-guide-config]')?.textContent ?? '{}');
      const sectionLinks = Object.fromEntries(config.sections.map((section) => [section.productId, `#${section.anchor}`]));
      this.querySelectorAll<AbProductsElement>('[data-guide-slider] ab-products').forEach((slider) => {
        slider.sectionLinks = sectionLinks;
      });
      if (!config.sections.length) return;
      const products = await ProductsApi.getProductSlim(config.source, { include: 'guide-details' });
      if (generation !== this.#generation) return;
      const byId = new Map(products.map((product) => [product._id, product]));
      config.sections.forEach((section) => {
        const root = this.#sectionRoot(section.anchor);
        if (!root) return;
        const product = byId.get(section.productId);
        if (product) this.#render(root, product, section);
        else this.#status(root, 'Dati prodotto non disponibili nella selezione di questa guida.');
      });
      this.querySelectorAll<AbStickyTocElement>('ab-sticky-toc').forEach((toc) => toc.refresh());
    } catch {
      if (generation !== this.#generation) return;
      this.querySelectorAll<HTMLElement>('[data-guide-section]').forEach((root) =>
        this.#status(root, 'Non è stato possibile caricare i dati prodotto. Riprova aggiornando la pagina.'));
    }
  }

  #sectionRoot(anchor: string) {
    return Array.from(this.querySelectorAll<HTMLElement>('[data-guide-section]')).find((root) => root.id === anchor);
  }

  #status(root: HTMLElement, message: string) {
    root.querySelectorAll<HTMLElement>('[data-guide-summary], [data-guide-affiliate]').forEach((slot) => {
      const status = el('p', 'ab-buying-guide__status', { role: 'status' });
      status.textContent = message;
      slot.replaceChildren(status);
    });
  }

  #render(root: HTMLElement, product: ProductSlimResponseDTO, section: GuideSection) {
    const view = resolveGuideProduct(product, section);
    const title = root.querySelector<HTMLElement>('[data-guide-title]');
    if (title) { title.textContent = view.title ?? ''; title.hidden = view.title === undefined; }
    const name = view.title ?? product.productData.names[0];
    this.querySelectorAll<HTMLAnchorElement>('a[data-guide-product-link]').forEach((link) => {
      if (link.hash === `#${section.anchor}`) link.textContent = name;
    });
    root.querySelectorAll<HTMLElement>('[data-guide-image]').forEach((slot) => {
      slot.replaceChildren(...(view.image ? [this.#image(view.image)] : []));
    });

    const model = createProductCardViewModel(createProductListingItem(product));
    root.querySelectorAll('[data-guide-summary]').forEach((slot) => {
      const summary = el('ab-product-summary') as AbProductSummaryElement;
      summary.pros = view.pros ?? null;
      summary.cons = view.cons ?? null;
      summary.aspects = view.aspects ?? null;
      summary.cardModel = model;
      slot.replaceChildren(summary);
    });

    root.querySelectorAll('[data-guide-affiliate]').forEach((slot) => {
      const box = el('ab-product-affiliate-box') as AbProductAffiliateBoxElement;
      box.productTitle = view.title ?? null;
      box.image = view.image ?? null;
      box.descriptionPoints = view.descriptionPoints ?? null;
      box.cardModel = model;
      slot.replaceChildren(box);
    });
  }

  #image(image: { url: string; alt: string }) {
    return el('img', 'ab-buying-guide__product-image', { src: image.url, alt: image.alt, loading: 'lazy', decoding: 'async' });
  }

}

export function defineAbBuyingGuideElement() {
  if (!customElements.get('ab-buying-guide')) customElements.define('ab-buying-guide', AbBuyingGuideElement);
}
